import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { bayBounds, canWalk, entryPose } from './planner-geometry.js';

// Kept independent of React: the existing Office screen owns all app state.
const COLORS = {
  backdrop: '#e9e6de', floor: '#ddd9cf', wall: '#f4f1e9', edge: '#b6aea0',
  oak: '#b58d60', charcoal: '#354442', metal: '#e8e6df', teal: '#348a7a',
  products: ['#ce9e56', '#748e78', '#c8b78e', '#a95844', '#688788', '#e1d8bd', '#b58b98', '#425f59'],
};

export function createPlanner3D(host, initial) {
  let props = initial;
  let disposed = false;
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(COLORS.backdrop);
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.domElement.setAttribute('aria-label', 'Interactive 3D store floor plan');
  renderer.domElement.setAttribute('role', 'img');
  renderer.domElement.tabIndex = 0;
  host.appendChild(renderer.domElement);

  const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 180);
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.12;
  controls.minDistance = 2.5;
  controls.maxDistance = 65;
  controls.maxPolarAngle = Math.PI / 2 - 0.035;
  controls.target.set(12, 0, 6);
  controls.screenSpacePanning = false;

  scene.add(new THREE.HemisphereLight('#f7f5ec', '#a29c8b', 2.1));
  const sun = new THREE.DirectionalLight('#fff5df', 3.6);
  sun.position.set(3, 19, 12);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  Object.assign(sun.shadow.camera, { left: -21, right: 21, top: 18, bottom: -18, near: 0.5, far: 65 });
  sun.shadow.normalBias = 0.035;
  sun.shadow.bias = -0.0003;
  sun.shadow.radius = 3;
  sun.target.position.set(12, 0, 5);
  scene.add(sun, sun.target);
  const fill = new THREE.DirectionalLight('#dae8ef', 1.2);
  fill.position.set(24, 12, -8);
  scene.add(fill);

  const boxGeo = new THREE.BoxGeometry(1, 1, 1);
  const cylinderGeo = new THREE.CylinderGeometry(0.5, 0.5, 1, 10);
  const baseMaterials = {};
  const resources = new Set([boxGeo, cylinderGeo]);
  function material(key, color, extra = {}) {
    if (!baseMaterials[key]) {
      baseMaterials[key] = new THREE.MeshStandardMaterial({ color, roughness: 0.75, ...extra });
      resources.add(baseMaterials[key]);
    }
    return baseMaterials[key];
  }
  const mats = {
    wall: material('wall', COLORS.wall), oak: material('oak', COLORS.oak),
    steel: material('steel', COLORS.metal, { metalness: 0.35, roughness: 0.35 }),
    dark: material('dark', COLORS.charcoal, { roughness: 0.55 }),
    white: material('white', '#f9f5e9'), stone: material('stone', '#d2c8b9'),
    product: material('product', '#ffffff', { roughness: 0.45 }),
    label: material('label', '#ffffff', { roughness: .8 }),
  };
  // Procedural oak grain and ceramic grout, with no external texture requests.
  const woodCanvas = document.createElement('canvas');
  woodCanvas.width = 128; woodCanvas.height = 256;
  const wc = woodCanvas.getContext('2d');
  wc.fillStyle = '#ceb18a'; wc.fillRect(0, 0, 128, 256);
  for (let i = 0; i < 100; i++) {
    wc.strokeStyle = `rgba(95,63,32,${0.035 + (i % 7) * 0.008})`;
    wc.beginPath(); wc.moveTo(i * 1.37, 0); wc.bezierCurveTo(i * 1.37 + 9, 80, i * 1.37 - 8, 160, i * 1.37, 256); wc.stroke();
  }
  const woodTexture = new THREE.CanvasTexture(woodCanvas);
  woodTexture.colorSpace = THREE.SRGBColorSpace;
  mats.oak.map = woodTexture; mats.oak.color.set('#ffffff'); resources.add(woodTexture);
  const labelCanvas = document.createElement('canvas'); labelCanvas.width = 256; labelCanvas.height = 160;
  const lc = labelCanvas.getContext('2d');
  lc.fillStyle = '#eee8d8'; lc.fillRect(0, 0, 256, 160);
  lc.fillStyle = '#335448'; lc.fillRect(0, 0, 256, 23);
  lc.font = 'bold 42px Georgia'; lc.textAlign = 'center'; lc.fillText('daily', 128, 75);
  lc.font = '15px Arial'; lc.fillText('E S S E N T I A L S', 128, 102);
  lc.font = '11px Arial'; lc.fillText('QUALITY · EVERY DAY', 128, 125);
  for (let i = 0; i < 34; i++) lc.fillRect(76 + i * 3, 136, i % 3 === 0 ? 2 : 1, 13);
  const labelTexture = new THREE.CanvasTexture(labelCanvas); labelTexture.colorSpace = THREE.SRGBColorSpace;
  mats.label.map = labelTexture; resources.add(labelTexture);
  const tileCanvas = document.createElement('canvas'); tileCanvas.width = tileCanvas.height = 128;
  const tc = tileCanvas.getContext('2d');
  tc.fillStyle = '#d8d5cd'; tc.fillRect(0, 0, 128, 128);
  for (let i = 0; i < 1600; i++) {
    const v = (i * 73) % 128, u = (i * 47 + Math.floor(i / 128) * 11) % 128;
    tc.fillStyle = i % 2 ? 'rgba(255,255,255,.08)' : 'rgba(96,86,70,.03)'; tc.fillRect(u, v, 2, 2);
  }
  tc.strokeStyle = '#c6c2b9'; tc.lineWidth = 1.1; tc.strokeRect(0, 0, 128, 128);
  const floorTexture = new THREE.CanvasTexture(tileCanvas);
  floorTexture.wrapS = floorTexture.wrapT = THREE.RepeatWrapping;
  floorTexture.repeat.set(24, 12); floorTexture.colorSpace = THREE.SRGBColorSpace;
  resources.add(floorTexture);

  let world = new THREE.Group(); scene.add(world);
  let temporary = new Set();
  let batches = new Map();
  let pickables = [], bayVisuals = [], labels = [], cutawayWalls = [], blockers = [];
  let currentPlan = null, currentView = null, currentNavigation = null, fittedAspect = 1, preserveResizeUntil = 0;
  const heldKeys = new Set();
  let yaw = -Math.PI / 2, pitch = 0, lastTime = performance.now(), lastPosition = '', lastPublish = 0;
  const isWalking = () => props.navigation === 'walk';
  const matrix = new THREE.Matrix4(), quaternion = new THREE.Quaternion();
  function box(x, y, z, w, h, d, mat = mats.wall, color, rotation = 0, shape = 'box') {
    const key = mat.uuid + shape;
    if (!batches.has(key)) batches.set(key, { mat, shape, items: [] });
    batches.get(key).items.push({ x, y, z, w, h, d, color, rotation });
  }
  function flush() {
    for (const { mat, shape, items } of batches.values()) {
      const mesh = new THREE.InstancedMesh(shape === 'box' ? boxGeo : cylinderGeo, mat, items.length);
      items.forEach((v, i) => {
        quaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), v.rotation);
        matrix.compose(new THREE.Vector3(v.x, v.y, v.z), quaternion, new THREE.Vector3(v.w, v.h, v.d));
        mesh.setMatrixAt(i, matrix);
        mesh.setColorAt(i, new THREE.Color(v.color || '#ffffff'));
      });
      mesh.castShadow = mat !== mats.product && mat !== mats.white;
      mesh.receiveShadow = true;
      world.add(mesh);
    }
    batches.clear();
  }
  function textSign(text, position, width = 2.2, rotation = 0, foreground = '#3f514c', background = '#f6f3eb') {
    const canvas = document.createElement('canvas'); canvas.width = 512; canvas.height = 100;
    const c = canvas.getContext('2d');
    c.fillStyle = background; c.fillRect(0, 0, 512, 100);
    c.fillStyle = foreground; c.font = '500 38px Arial'; c.textAlign = 'center'; c.textBaseline = 'middle';
    c.fillText(text, 256, 51, 476);
    const texture = new THREE.CanvasTexture(canvas); texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
    const mat = new THREE.MeshStandardMaterial({ map: texture, roughness: .85 });
    const sign = new THREE.Group(); sign.position.set(...position); sign.rotation.y = rotation;
    const height = width * 100 / 512, thickness = .035;
    const backing = new THREE.Mesh(boxGeo, mats.white);
    backing.scale.set(width, height, thickness); backing.castShadow = true; backing.receiveShadow = true; sign.add(backing);
    const faceGeo = new THREE.PlaneGeometry(width, height);
    // Separate outward-facing faces keep reverse-side lettering readable, with
    // a 2 mm clearance from the backing to avoid coplanar depth flicker.
    for (const side of [-1, 1]) {
      const face = new THREE.Mesh(faceGeo, mat);
      face.position.z = side * (thickness / 2 + .002);
      face.rotation.y = side < 0 ? Math.PI : 0;
      face.receiveShadow = true; sign.add(face);
    }
    world.add(sign); temporary.add(texture); temporary.add(mat); temporary.add(faceGeo); labels.push(sign);
    return sign;
  }
  function roomFurniture(room) {
    const { x, y, w, h, kind } = room;
    if (kind === 'office') {
      box(x + w * .55, .76, y + h * .48, w * .6, .08, .7, mats.oak);
      for (const dx of [-.48, .48]) box(x + w * .55 + dx, .38, y + h * .48, .045, .76, .6, mats.dark);
      box(x + w * .55, 1.08, y + h * .4, .55, .34, .035, mats.dark);
      box(x + w * .55, .82, y + h * .48, .2, .06, .22, mats.dark);
      box(x + w * .55, .45, y + h * .75, .48, .12, .48, mats.dark);
      box(x + w * .55, .74, y + h * .82, .48, .5, .07, mats.dark);
    } else if (kind === 'staff') {
      box(x + w * .5, .76, y + h * .6, 1.45, .08, 1.45, mats.oak, null, 0, 'cylinder');
      box(x + w * .5, .37, y + h * .6, .16, .7, .16, mats.dark, null, 0, 'cylinder');
      for (let i = 0; i < 4; i++) {
        const a = i * Math.PI / 2;
        box(x + w * .5 + Math.cos(a), .45, y + h * .6 + Math.sin(a), .45, .1, .45, mats.dark);
      }
      box(x + w * .5, .5, y + .45, w - .4, 1, .5, mats.oak);
      box(x + w * .5, 1.02, y + .45, w - .3, .06, .6, mats.stone);
    } else if (kind === 'amenities') {
      for (const dx of [.6, 1.6]) {
        box(x + dx, .38, y + .75, .5, .65, .65, mats.white, null, 0, 'cylinder');
        box(x + dx, .8, y + .35, .5, .5, .2, mats.white);
      }
      box(x + w / 2, .8, y + h - .6, 1.7, .15, .45, mats.white);
    } else {
      box(x + .45, 1, y + h / 2, .45, 2, h - .5, mats.oak);
    }
  }
  function createBay(fixture, bay, index) {
    const b = bayBounds(fixture, index);
    const vertical = fixture.h > fixture.w;
    const length = vertical ? b.h : b.w, depth = vertical ? b.w : b.h;
    const cx = b.x + b.w / 2, cz = b.y + b.h / 2;
    const height = fixture.height || 1.55;
    const localBox = (u, v, a, w, h, d, mat, color, shape) => {
      box(cx + (vertical ? a : u), v, cz + (vertical ? u : a), vertical ? d : w, h, vertical ? w : d, mat, color, 0, shape);
    };
    localBox(0, .11, 0, length - .035, .22, depth, mats.dark);
    localBox(0, height / 2, 0, length - .06, height, .07, mats.oak);
    // End panels sit on the plinth: overlapping coplanar outer faces
    // otherwise z-fight across the bottom 22 cm as the camera moves.
    const plinthHeight = .22;
    for (const end of [-1, 1]) localBox(end * (length / 2 - .04), (height + plinthHeight) / 2, 0, .045, height - plinthHeight, depth, mats.steel);
    const shelves = 4;
    const count = Math.max(3, Math.floor(length / .19));
    for (let row = 0; row < shelves; row++) {
      const sy = .26 + row * (height - .35) / shelves;
      localBox(0, sy, 0, length - .05, .035, depth, mats.steel);
      for (const side of [-1, 1]) {
        localBox(0, sy + .015, side * (depth / 2 - .018), length - .06, .065, .025, mats.white);
        for (let ticket = 0; ticket < 3; ticket++) localBox((ticket - 1) * length / 3, sy + .023, side * (depth / 2 + .001), .105, .026, .008, mats.dark);
        if (!bay.cat) continue;
        for (let item = 0; item < count; item++) {
          const hash = (index * 13 + row * 7 + item * 3 + fixture.id.length) % COLORS.products.length;
          const ph = .15 + ((item + row * 3) % 4) * .025;
          const pw = length / count * .76;
          const u = -length / 2 + (item + .5) * length / count;
          const a = side * (depth * .28);
          const bottle = (item + row + index) % 3 === 0;
          localBox(u, sy + ph / 2 + .02, a, pw, ph, .15, mats.product, COLORS.products[hash], bottle ? 'cylinder' : 'box');
          if (bottle) localBox(u, sy + ph + .035, a, pw * .6, .04, .09, mats.white, null, 'cylinder');
          localBox(u, sy + ph * .48, a + side * .079, pw * .83, ph * .55, .007, mats.label);
        }
      }
    }
    localBox(0, height + .015, 0, length, .055, .15, mats.oak);

    const pickMat = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false });
    const pick = new THREE.Mesh(new THREE.BoxGeometry(b.w, height + .1, b.h), pickMat);
    pick.position.set(cx, (height + .1) / 2, cz);
    pick.userData = { fixtureId: fixture.id, bayId: bay.id, label: fixture.label, index, bounds: b };
    world.add(pick); pickables.push(pick); temporary.add(pick.geometry); temporary.add(pickMat);
    const edgeMat = new THREE.LineBasicMaterial({ color: COLORS.teal, transparent: true, opacity: 1, depthTest: false });
    const edges = new THREE.LineSegments(new THREE.EdgesGeometry(pick.geometry), edgeMat);
    edges.position.copy(pick.position); edges.visible = false; edges.renderOrder = 6; world.add(edges);
    temporary.add(edges.geometry); temporary.add(edgeMat);
    const overlayMat = new THREE.MeshBasicMaterial({ transparent: true, opacity: .68, depthWrite: false, side: THREE.DoubleSide });
    const overlay = new THREE.Mesh(new THREE.PlaneGeometry(b.w - .04, b.h - .04), overlayMat);
    overlay.rotation.x = -Math.PI / 2; overlay.position.set(cx, height + .055, cz); world.add(overlay);
    temporary.add(overlay.geometry); temporary.add(overlayMat);
    bayVisuals.push({ fixture, bay, pick, edges, overlay });
  }
  function createDispensingRobot(f) {
    const x = f.x + f.w / 2, z = f.y + f.h / 2, height = f.height;
    const faceX = value => f.face === 'east' ? 2 * x - value : value;
    const robotBox = (px, ...args) => box(faceX(px), ...args);
    // Generic enclosed pharmacy storage/retrieval cabinet, not a vendor model.
    robotBox(x, .13, z, f.w, .26, f.h, mats.dark);
    robotBox(x, height - .10, z, f.w, .2, f.h, mats.white);
    const panelHeight = height - .46, panelY = .26 + panelHeight / 2;
    robotBox(f.x + f.w - .045, panelY, z, .09, panelHeight, f.h - .18, mats.white);
    for (const end of [-1, 1]) robotBox(x, panelY, z + end * (f.h / 2 - .045), f.w, panelHeight, .09, mats.white);
    const glassMat = new THREE.MeshPhysicalMaterial({ color: '#b3d1cc', roughness: .16, transparent: true, opacity: .18, depthWrite: false });
    temporary.add(glassMat);
    for (let section = 0; section < 4; section++) {
      const sz = f.y + (section + .5) * f.h / 4;
      for (let row = 0; row < 5; row++) {
        const sy = .4 + row * .32;
        robotBox(x + .12, sy, sz, f.w - .38, .025, f.h / 4 - .12, mats.steel);
        for (let item = 0; item < 4; item++) {
          const pz = sz - .52 + item * .34;
          robotBox(x + .12, sy + .12, pz, .28, .21, .22, mats.product, COLORS.products[(row + item + section) % COLORS.products.length]);
        }
      }
      const glass = new THREE.Mesh(boxGeo, glassMat);
      glass.position.set(faceX(f.x + .012), 1.19, sz); glass.scale.set(.018, 1.78, f.h / 4 - .08); world.add(glass);
      robotBox(f.x + .035, 1.2, sz - f.h / 8 + .025, .07, 1.85, .05, mats.steel);
    }
    // Visible horizontal track, moving carriage and retrieval fork inside glazing.
    robotBox(f.x + .17, .6, z, .08, .06, f.h - .2, mats.dark);
    robotBox(f.x + .17, 1.9, z, .08, .045, f.h - .2, mats.dark);
    robotBox(f.x + .2, 1.24, z + .45, .12, 1.35, .08, mats.steel);
    robotBox(f.x + .23, 1.2, z + .45, .28, .24, .3, mats.dark);
    for (const offset of [-.10, .10]) robotBox(f.x + .45, 1.1, z + .45 + offset, .35, .04, .045, mats.steel);
    // Delivery hatch and control panel face the staff passage.
    robotBox(f.x - .018, .57, f.y + f.h - .43, .04, .28, .52, mats.dark);
    robotBox(f.x - .043, .47, f.y + f.h - .43, .08, .045, .48, mats.steel);
    robotBox(f.x - .018, 1.45, f.y + f.h - .43, .04, .35, .28, mats.dark);
    robotBox(f.x - .041, 1.45, f.y + f.h - .43, .006, .27, .22, mats.product, COLORS.teal);
    textSign('DISPENSING ROBOT', [faceX(f.x - .025), 2.15, z], 1.9, Math.PI / 2);
  }
  function build(plan) {
    scene.remove(world);
    world.traverse(o => { if (o.isInstancedMesh) o.dispose(); });
    for (const r of temporary) r.dispose();
    temporary.clear();
    world = new THREE.Group(); scene.add(world);
    pickables = []; bayVisuals = []; labels = []; cutawayWalls = []; blockers = []; batches.clear();
    const { w, h } = plan.room;
    const floorMat = new THREE.MeshStandardMaterial({ map: floorTexture, roughness: .85 });
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(w, h), floorMat);
    floor.rotation.x = -Math.PI / 2; floor.position.set(w / 2, 0, h / 2); floor.receiveShadow = true;
    world.add(floor); temporary.add(floor.geometry); temporary.add(floorMat);
    if (isWalking()) {
      const ceilingMat = new THREE.MeshBasicMaterial({ color: '#e6e3db', toneMapped: false }); temporary.add(ceilingMat);
      const ceiling = new THREE.Mesh(boxGeo, ceilingMat); ceiling.position.set(w / 2, 2.95, h / 2); ceiling.scale.set(w, .1, h); world.add(ceiling);
      for (const x of [6.5, 11, 16, 21]) {
        box(x, 2.86, 7.2, .07, .035, 8.8, mats.dark);
        box(x, 2.835, 7.2, .045, .02, 8.65, mats.white);
      }
    }
    box(w / 2, -.17, h / 2, w + .3, .32, h + .3, mats.stone);
    box(w / 2, 1.4, 0, w + .2, 2.8, .18, mats.wall);
    box(0, 1.4, h / 2, .18, 2.8, h, mats.wall);
    const entrance = plan.room.entrance;
    const frontSegments = entrance.side === 'bottom'
      ? [[0, entrance.x], [entrance.x + entrance.w, w]] : [[0, w]];
    const rightSegments = entrance.side === 'right'
      ? [[0, entrance.y], [entrance.y + entrance.w, h]] : [[0, h]];
    const outerWalls = [];
    for (const [start, end] of frontSegments) {
      if (end <= start) continue;
      box((start + end) / 2, .08, h, end - start, .16, .16, mats.stone);
      outerWalls.push([(start + end) / 2, 1.4, h, end - start, 2.8, .12]);
    }
    for (const [start, end] of rightSegments) {
      if (end <= start) continue;
      box(w, .08, (start + end) / 2, .16, .16, end - start, mats.stone);
      outerWalls.push([w, 1.4, (start + end) / 2, .12, 2.8, end - start]);
    }
    for (const dims of outerWalls) {
      const wall = new THREE.Mesh(boxGeo, mats.wall); wall.position.set(...dims.slice(0, 3)); wall.scale.set(...dims.slice(3));
      wall.castShadow = true; wall.receiveShadow = true; wall.visible = false; world.add(wall); cutawayWalls.push(wall);
    }
    // A pale dispensing-zone floor follows the reference counter and robot.
    box(3.65, .012, 7.55, 6.8, .02, 7.9, mats.stone);
    for (const r of plan.rooms || []) {
      const wallH = isWalking() ? 2.8 : 1.8;
      box(r.x + r.w / 2, .025, r.y + r.h / 2, r.w, .04, r.h, mats.stone);
      box(r.x + r.w, wallH / 2, r.y + r.h / 2, .12, wallH, r.h, mats.wall);
      const door = .9;
      box(r.x + (r.w - door) / 2, wallH / 2, r.y + r.h, r.w - door, wallH, .12, mats.wall);
      box(r.x + r.w - door / 2, wallH - .06, r.y + r.h, door, .12, .12, mats.oak);
      roomFurniture(r);
      textSign(r.label, [r.x + (r.w - .9) / 2, 1.55, r.y + r.h + .085], Math.min(1.3, (r.w - .9) * .82));
    }
    for (const f of plan.fixtures) {
      if (f.bays.length) {
        f.bays.forEach((b, i) => createBay(f, b, i));
        if (f.type !== 'wallbay') {
          const signWidth = Math.min(1.4, Math.max(f.w, f.h) * .7);
          textSign(f.label, [f.x + f.w / 2, f.height + .045 + signWidth * 100 / 512 / 2, f.y + f.h / 2], signWidth, f.h > f.w ? Math.PI / 2 : 0);
        }
      } else if (f.type === 'robot') {
        createDispensingRobot(f);
      } else if (f.type === 'display') {
        // Rounded, glass-topped cooler in the upper-middle-right of the drawing.
        const shape = new THREE.Shape(), r = f.h / 2;
        shape.moveTo(r, 0); shape.lineTo(f.w - r, 0); shape.absarc(f.w - r, r, r, -Math.PI / 2, Math.PI / 2, false);
        shape.lineTo(r, f.h); shape.absarc(r, r, r, Math.PI / 2, Math.PI * 1.5, false);
        const geo = new THREE.ExtrudeGeometry(shape, { depth: .9, bevelEnabled: true, bevelSize: .035, bevelThickness: .03, bevelSegments: 2, steps: 1 });
        const cooler = new THREE.Mesh(geo, mats.white); cooler.rotation.x = -Math.PI / 2; cooler.position.set(f.x, 0, f.y + f.h); cooler.castShadow = true; cooler.receiveShadow = true; world.add(cooler); temporary.add(geo);
        box(f.x + f.w / 2, .93, f.y + f.h / 2, f.w - .8, .045, f.h - .16, mats.dark);
        for (let k = 0; k < 5; k++) box(f.x + .55 + k * .4, .97, f.y + f.h / 2, .32, .025, .65, mats.product, COLORS.products[k]);
        const glass = new THREE.MeshPhysicalMaterial({ color: '#b7d5cc', metalness: 0, roughness: .08, transparent: true, opacity: .3 });
        const top = new THREE.Mesh(boxGeo, glass); top.position.set(f.x + f.w / 2, 1.03, f.y + f.h / 2); top.scale.set(f.w - .7, .06, f.h - .12); world.add(top); temporary.add(glass);
      } else {
        const x = f.x + f.w / 2, z = f.y + f.h / 2;
        box(x, f.height / 2, z, f.w, f.height, f.h, f.type === 'fridge' ? mats.dark : mats.oak);
        box(x, f.height + .035, z, f.w + .07, .07, f.h + .07, mats.stone);
        if (f.type === 'checkout' || f.type === 'dispense') {
          // Dispensing operators face east with the robot behind them;
          // checkout operators face west with stock behind them.
          const operatorSide = f.type === 'dispense' ? -1 : 1;
          // Checkout and dispensing each have their own three-station worktop.
          for (let station = 0; station < f.stations; station++) {
            const pz = f.y + (station + .5) * f.h / f.stations;
            box(x, f.height + .05, pz, .3, .045, .25, mats.dark);
            box(x, f.height + .2, pz, .055, .3, .06, mats.dark);
            box(x, f.height + .38, pz, .055, .32, .48, mats.dark);
            box(x + operatorSide * .031, f.height + .38, pz, .006, .26, .41, mats.steel);
            box(x + operatorSide * .24, f.height + .065, pz, .2, .035, .35, mats.dark);
            box(x - operatorSide * .24, f.height + .08, pz + .32, .14, .09, .12, mats.dark);
            if (f.type === 'dispense') {
              box(x + operatorSide * .12, f.height + .15, pz + .55, .3, .2, .32, mats.white);
              box(x + operatorSide * .28, f.height + .12, pz + .55, .025, .07, .24, mats.dark);
            }
          }
        }
        if (f.type === 'fridge') {
          for (let k = 0; k < 8; k++) {
            box(x, f.height + .12, f.y + .4 + k * .65, .48, .14, .48, mats.white);
            box(x, f.height + .2, f.y + .4 + k * .65, .35, .1, .35, mats.product, COLORS.products[k % 8]);
          }
        }
      }
    }
    // Corner doorway, mat and Walk pose all follow the same plan entry.
    const e = plan.room.entrance, bottomEntry = e.side === 'bottom';
    const ex = bottomEntry ? e.x + e.w / 2 : e.x;
    const ez = bottomEntry ? e.y : e.y + e.w / 2;
    box(ex - (bottomEntry ? 0 : .85), .025, ez - (bottomEntry ? .85 : 0), bottomEntry ? e.w - .12 : 1.6, .035, bottomEntry ? 1.6 : e.w - .12, mats.dark);
    for (const side of [-1, 1]) box(ex + (bottomEntry ? side * e.w / 2 : 0), 1.35, ez + (bottomEntry ? 0 : side * e.w / 2), .065, 2.7, .065, mats.dark);
    box(ex, 2.7, ez, bottomEntry ? e.w + .05 : .065, .065, bottomEntry ? .065 : e.w + .05, mats.dark);
    textSign('ENTRANCE', [ex, 2.51, ez], 1.6, bottomEntry ? 0 : Math.PI / 2, '#ffffff', '#3f635a');
    textSign('DISPENSING', [.11, 2.1, 7.5], 2.4, Math.PI / 2);
    // Warm linear luminaire over the dispensing bench.
    box(2.4, 2.5, 7.4, .12, .06, 5.4, mats.white);
    flush();
    // A small set of occlusion volumes avoids raycasting thousands of products.
    const block = (x, y, z, w, h, d) => {
      const mesh = new THREE.Mesh(boxGeo, mats.wall); mesh.position.set(x, y, z); mesh.scale.set(w, h, d); mesh.updateMatrixWorld(true); blockers.push(mesh);
    };
    block(w / 2, 1.4, 0, w, 2.8, .18); block(0, 1.4, h / 2, .18, 2.8, h);
    blockers.push(...cutawayWalls);
    for (const r of plan.rooms || []) {
      const rh = isWalking() ? 2.8 : 1.8;
      block(r.x + r.w, rh / 2, r.y + r.h / 2, .12, rh, r.h);
      block(r.x + (r.w - .9) / 2, rh / 2, r.y + r.h, r.w - .9, rh, .12);
    }
    for (const f of plan.fixtures.filter(f => !f.bays.length)) block(f.x + f.w / 2, f.height / 2, f.y + f.h / 2, f.w, f.height, f.h);
  }
  function updateBayStyles() {
    for (const v of bayVisuals) {
      const selected = props.ctx.selBay?.fixtureId === v.fixture.id && props.ctx.selBay?.bayId === v.bay.id;
      v.edges.visible = selected;
      const currentBay = props.plan.fixtures.find(f => f.id === v.fixture.id)?.bays.find(b => b.id === v.bay.id) || v.bay;
      v.overlay.visible = props.ctx.mode === 'perf' || selected;
      const color = props.ctx.mode === 'perf' ? props.colorForBay(currentBay) : COLORS.teal;
      v.overlay.material.color.set(color || '#babbb3');
      v.overlay.material.opacity = props.ctx.mode === 'perf' ? .8 : .18;
    }
    labels.forEach(l => { l.visible = props.ctx.showLabels; });
    cutawayWalls.forEach(w => { w.visible = (isWalking() || !!props.fullWalls) && props.ctx.view !== 'top'; });
  }
  function preset(name) {
    fittedAspect = camera.aspect;
    camera.fov = 38; camera.updateProjectionMatrix();
    controls.enableRotate = name !== 'top';
    if (name === 'entry') {
      const entry = entryPose(props.plan);
      camera.position.set(entry.x, 2.3, entry.z); controls.target.set(entry.x + Math.sin(entry.yaw) * 8, 1.15, entry.z - Math.cos(entry.yaw) * 8);
    } else if (name === 'top') {
      const distance = Math.max(23, 14 / Math.max(camera.aspect, .5) / Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)));
      camera.position.set(12, distance, 6.001); controls.target.set(12, 0, 6);
    } else {
      const distance = Math.max(25, 15 / Math.max(camera.aspect, .5) / Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)));
      controls.target.set(12, .3, 5.8);
      camera.position.copy(controls.target).add(new THREE.Vector3(0.18, .88, 1).normalize().multiplyScalar(distance));
    }
    controls.update();
  }
  function look() {
    camera.lookAt(camera.position.x + Math.sin(yaw) * Math.cos(pitch), camera.position.y + Math.sin(pitch), camera.position.z - Math.cos(yaw) * Math.cos(pitch));
  }
  function startWalking() {
    controls.enabled = false; heldKeys.clear();
    camera.fov = 65; camera.updateProjectionMatrix(); renderer.domElement.focus({ preventScroll: true });
    const entry = entryPose(props.plan);
    camera.position.set(entry.x, 1.65, entry.z); yaw = entry.yaw; pitch = -.035; look();
  }
  function step(forward, strafe, dt = .18) {
    const speed = 2.1 * dt;
    const dx = (Math.sin(yaw) * forward + Math.cos(yaw) * strafe) * speed;
    const dz = (-Math.cos(yaw) * forward + Math.sin(yaw) * strafe) * speed;
    // Axis separation lets the player slide along fixture edges. Small substeps
    // avoid tunnelling through thin room walls after a slow browser frame.
    const steps = Math.max(1, Math.ceil(Math.hypot(dx, dz) / .08));
    for (let i = 0; i < steps; i++) {
      if (canWalk(camera.position.x + dx / steps, camera.position.z, props.plan)) camera.position.x += dx / steps;
      if (canWalk(camera.position.x, camera.position.z + dz / steps, props.plan)) camera.position.z += dz / steps;
    }
    look();
  }
  function resize() {
    const w = Math.max(1, host.clientWidth), h = Math.max(1, host.clientHeight);
    renderer.setSize(w, h, false); camera.aspect = w / h; camera.updateProjectionMatrix();
    if (performance.now() < preserveResizeUntil) fittedAspect = camera.aspect;
    else if (currentView && !isWalking() && Math.abs(fittedAspect - camera.aspect) > .15) preset(props.ctx.view === 'top' ? 'top' : 'overview');
  }
  const observer = new ResizeObserver(resize); observer.observe(host); resize();

  const raycaster = new THREE.Raycaster(), pointer = new THREE.Vector2();
  function hit(event) {
    const rect = renderer.domElement.getBoundingClientRect();
    pointer.set((event.clientX - rect.left) / rect.width * 2 - 1, -(event.clientY - rect.top) / rect.height * 2 + 1);
    raycaster.setFromCamera(pointer, camera);
    const candidate = raycaster.intersectObjects(pickables, false)[0];
    if (!candidate) return null;
    const occluder = raycaster.intersectObjects(blockers.filter(o => o.visible), false)[0];
    return occluder && occluder.distance + .12 < candidate.distance ? null : candidate.object;
  }
  let down = null, lastPointer = null;
  const pointerDown = e => { down = [e.clientX, e.clientY]; lastPointer = down; renderer.domElement.focus(); };
  const pointerUp = e => {
    if (!down || Math.hypot(e.clientX - down[0], e.clientY - down[1]) > 5 || e.button !== 0) { down = null; return; }
    down = null; const object = hit(e);
    if (object) props.ctx.onSelectBay(object.userData.fixtureId, object.userData.bayId);
  };
  const pointerMove = e => {
    if (isWalking() && down && e.buttons === 1) {
      yaw += (e.clientX - lastPointer[0]) * .004;
      pitch = THREE.MathUtils.clamp(pitch - (e.clientY - lastPointer[1]) * .004, -.95, .95);
      lastPointer = [e.clientX, e.clientY]; look();
    }
    const object = hit(e); host.style.cursor = object ? 'pointer' : 'grab';
    props.onHover?.(object ? `${object.userData.label} · Bay ${object.userData.index + 1}` : '');
  };
  const dragOver = e => { if (props.ctx.dragCat) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; } };
  const drop = e => { e.preventDefault(); const o = hit(e); if (o && props.ctx.dragCat) props.ctx.onDrop(o.userData.fixtureId, o.userData.bayId); };
  const keyboard = e => {
    if (isWalking()) {
      const key = e.key.toLowerCase();
      if (['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'].includes(key)) {
        e.preventDefault();
        if (!heldKeys.has(key)) step(['w', 'arrowup'].includes(key) ? 1 : ['s', 'arrowdown'].includes(key) ? -1 : 0, ['d', 'arrowright'].includes(key) ? 1 : ['a', 'arrowleft'].includes(key) ? -1 : 0, .06);
        heldKeys.add(key);
      }
      if (e.key === 'Escape') { heldKeys.clear(); props.onExitWalk?.(); }
      return;
    }
    if (e.key === '+' || e.key === '=') { e.preventDefault(); zoom(.85); }
    if (e.key === '-') { e.preventDefault(); zoom(1.18); }
    if (e.key.toLowerCase() === 'r') preset(props.ctx.view === 'top' ? 'top' : 'overview');
    if (e.key.toLowerCase() === 'f') focus();
  };
  for (const [event, fn] of Object.entries({ pointerdown: pointerDown, pointerup: pointerUp, pointermove: pointerMove, dragover: dragOver, drop, keydown: keyboard })) renderer.domElement.addEventListener(event, fn);
  const contextLost = e => { e.preventDefault(); props.onError?.('The 3D context was interrupted. Reload the planner to restore the view.'); };
  renderer.domElement.addEventListener('webglcontextlost', contextLost);
  const keyup = e => heldKeys.delete(e.key.toLowerCase());
  const clearKeys = () => heldKeys.clear();
  window.addEventListener('keyup', keyup); window.addEventListener('blur', clearKeys);
  renderer.domElement.addEventListener('blur', clearKeys);
  function zoom(factor) {
    const offset = camera.position.clone().sub(controls.target);
    offset.setLength(THREE.MathUtils.clamp(offset.length() * factor, controls.minDistance, controls.maxDistance));
    camera.position.copy(controls.target).add(offset); controls.update();
  }
  function focus() {
    const v = bayVisuals.find(v => props.ctx.selBay?.fixtureId === v.fixture.id && props.ctx.selBay?.bayId === v.bay.id);
    if (!v) return;
    const target = v.pick.position.clone();
    const direction = camera.position.clone().sub(controls.target).normalize();
    controls.target.copy(target); camera.position.copy(target).add(direction.multiplyScalar(5)); controls.update();
  }
  let frame;
  function render() {
    if (disposed) return;
    const now = performance.now(), dt = Math.min((now - lastTime) / 1000, .05); lastTime = now;
    if (isWalking()) {
      const forward = Number(heldKeys.has('w') || heldKeys.has('arrowup')) - Number(heldKeys.has('s') || heldKeys.has('arrowdown'));
      const strafe = Number(heldKeys.has('d') || heldKeys.has('arrowright')) - Number(heldKeys.has('a') || heldKeys.has('arrowleft'));
      if (forward || strafe) step(forward / (forward && strafe ? 1.414 : 1), strafe / (forward && strafe ? 1.414 : 1), dt);
      const positionKey = `${camera.position.x.toFixed(2)},${camera.position.z.toFixed(2)},${yaw.toFixed(2)}`;
      if (positionKey !== lastPosition && now - lastPublish > 120) {
        props.onPosition?.({ x: camera.position.x, z: camera.position.z, yaw }); lastPosition = positionKey; lastPublish = now;
      }
    } else {
      controls.update();
      const bounded = controls.target.clone(); bounded.x = THREE.MathUtils.clamp(bounded.x, -2, 26); bounded.z = THREE.MathUtils.clamp(bounded.z, -2, 14);
      camera.position.add(bounded.clone().sub(controls.target)); controls.target.copy(bounded);
    }
    labels.forEach(l => { l.visible = props.ctx.showLabels; });
    renderer.render(scene, camera); frame = requestAnimationFrame(render);
  }
  function update(next) {
    props = next;
    if (currentPlan !== next.plan || (currentNavigation === 'walk') !== isWalking()) { build(next.plan); currentPlan = next.plan; }
    if (currentView !== next.ctx.view) { preset(next.ctx.view === 'top' ? 'top' : 'overview'); currentView = next.ctx.view; }
    if (currentNavigation !== next.navigation) {
      if (isWalking()) startWalking();
      else { controls.enabled = true; heldKeys.clear(); if (currentNavigation === 'walk') preset(next.ctx.view === 'top' ? 'top' : 'overview'); }
      currentNavigation = next.navigation;
    }
    controls.mouseButtons.LEFT = next.navigation === 'pan' ? THREE.MOUSE.PAN : THREE.MOUSE.ROTATE;
    updateBayStyles();
  }
  update(initial); render();
  return {
    update, preset, focus, zoom,
    preserveCameraOnResize: () => { preserveResizeUntil = performance.now() + 1000; },
    walkStep: (forward, strafe) => { if (isWalking()) { step(forward, strafe); renderer.domElement.focus({ preventScroll: true }); } },
    turn: angle => { if (isWalking()) { yaw += angle; look(); renderer.domElement.focus({ preventScroll: true }); } },
    dispose() {
      disposed = true; cancelAnimationFrame(frame); observer.disconnect(); controls.dispose();
      renderer.domElement.removeEventListener('webglcontextlost', contextLost);
      window.removeEventListener('keyup', keyup); window.removeEventListener('blur', clearKeys); renderer.domElement.removeEventListener('blur', clearKeys);
      for (const [event, fn] of Object.entries({ pointerdown: pointerDown, pointerup: pointerUp, pointermove: pointerMove, dragover: dragOver, drop, keydown: keyboard })) renderer.domElement.removeEventListener(event, fn);
      world.traverse(o => { if (o.isInstancedMesh) o.dispose(); });
      for (const r of temporary) r.dispose(); for (const r of resources) r.dispose();
      sun.shadow.dispose(); renderer.dispose(); renderer.domElement.remove();
    },
  };
}

window.createPlanner3D = createPlanner3D;
